import React, { useEffect, useState } from 'react';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Body from '../components/Body';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import Navigation from '../components/Navigation';
import { Button, Alert, toast } from '@kontentino/ui';
import { gql, useMutation } from '@apollo/client';
import { isValidHttpUrl } from "../utils/url";
// reCAPTCHA v3 is used. We dynamically load and execute grecaptcha to obtain tokens.

const ADD_LINK = gql`
  mutation createLinkMutation(
  $input: NewLink!
) {
  createLink(
    input: $input
  ) {
    id
    shortUrl
  }
}
`;

const SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY as string | undefined;

const Home: React.FC = () => {
  const [url, setUrl] = useState('');
  const [validUrl, setValidUrl] = useState('');
  const [preview, setPreview] = useState<string | null>(null);
  const [hasError, setHasError] = useState(false);
  const [addNewLinkMutation, {loading}] = useMutation(ADD_LINK);

  // Preload reCAPTCHA v3 script on mount so the badge is visible
  useEffect(() => {
    if (SITE_KEY) {
      loadRecaptchaScript(SITE_KEY)
        .then(() => {
          const grecaptcha = (window as any).grecaptcha;
          if (grecaptcha && typeof grecaptcha.ready === 'function') {
            // Trigger ready to encourage the badge to render as soon as possible
            try { grecaptcha.ready(() => {}); } catch (_) {}
          }
        })
        .catch(() => {
          // ignore preload errors; will try again on submit
        });
    }
  }, []);

  const onUrlChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;

    setUrl(value);

    try {
      const urlToValidate = value.toLowerCase().startsWith('http') ? value : `https://${value}`;

      isValidHttpUrl(urlToValidate);

      setValidUrl(urlToValidate);
      setHasError(false);
    } catch (error) {
      setValidUrl('');
      setHasError(true);
    }
  };


  // Load reCAPTCHA v3 script once
  const loadRecaptchaScript = async (siteKey: string): Promise<void> => {
    if ((window as any).grecaptcha) return;
    await new Promise<void>((resolve, reject) => {
      const scriptId = 'recaptcha-v3-script';
      if (document.getElementById(scriptId)) return resolve();
      const script = document.createElement('script');
      script.id = scriptId;
      script.src = `https://www.google.com/recaptcha/api.js?render=${siteKey}`;
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Failed to load reCAPTCHA'));
      document.body.appendChild(script);
    });
  };

  const executeRecaptcha = async (action: string): Promise<string> => {
    if (!SITE_KEY) throw new Error('Missing site key');
    await loadRecaptchaScript(SITE_KEY);
    const grecaptcha = (window as any).grecaptcha;
    return new Promise<string>((resolve, reject) => {
      grecaptcha.ready(() => {
        grecaptcha.execute(SITE_KEY, { action }).then((token: string) => {
          resolve(token);
        }).catch(reject);
      });
    });
  };

  const handleAddNewLink = async (e: any) => {
    e.preventDefault();
    if (!SITE_KEY) {
      toast('Missing REACT_APP_RECAPTCHA_SITE_KEY. Please configure environment.', { type: 'error' });
      return;
    }
    if (url.length === 0) {
      toast('Please insert URL Address', { type: 'warning' });
      return;
    }

    try {
      const token = await executeRecaptcha('create_link');
      await addNewLinkMutation({
        variables: {
          input: {
            name: 'Link',
            url: validUrl,
            recaptchaToken: token,
          },
        },
      }).then(response => setPreview(response.data.createLink.shortUrl));
    } catch (error: any) {
      const message = error?.message || 'Failed to verify reCAPTCHA. Please try again.';
      toast(message, { type: 'error' });
    }
  }

  const onCopyUrlClick = () => {
    const el = document.createElement('textarea');
    el.value = preview ?? '';
    el.setAttribute('readonly', '');
    el.style.position = 'absolute';
    el.style.left = '-9999px';
    document.body.appendChild(el);
    el.select();
    document.execCommand('copy');
    document.body.removeChild(el);

    toast('URL copied to clipboard', {type: 'success'})
  }

  const handleClear = () => {
    setUrl('');
    setPreview(null);
  }

  return (
    <>
      <Navigation/>
      <Body>
        <Container>
          <Row>
            <Col>
              <h2>URL Shortener</h2>
            </Col>
          </Row>
          <Row>
            <Col>
              {
                hasError ? (
                  <Alert variant="warning">URL address is not valid!</Alert>
                ) : null
              }
              <Form>
                <Form.Group controlId="newURLAddress">
                  <Form.Label className='tw-font-medium tw-text-md'>URL Address</Form.Label>
                  <Form.Control required type="text" placeholder="https://" value={url} onChange={onUrlChange}/>
                  <Form.Control.Feedback type="invalid">This field is required.</Form.Control.Feedback>
                  <Form.Text className="text-muted" style={{wordBreak: 'break-all'}}>{validUrl}</Form.Text>
                  {!preview && (
                    <Button
                      type='submit'
                      onClick={(e: any) => handleAddNewLink(e)}
                      className='tw-mt-4 tw-ml-auto tw-flex'
                      disabled={hasError || !validUrl || loading}
                      isLoading={loading}
                    >
                      Shorten
                    </Button>
                  )}
                </Form.Group>
                <Form.Text className="text-muted tw-mt-2" style={{ display: 'block' }}>
                  This site is protected by reCAPTCHA and the Google
                  <a href="https://policies.google.com/privacy" target="_blank" rel="noreferrer"> Privacy Policy</a> and
                  <a href="https://policies.google.com/terms" target="_blank" rel="noreferrer"> Terms of Service</a> apply.
                </Form.Text>
              </Form>
            </Col>
          </Row>
          {preview && (
            <Row>
              <Col>
                <span className='tw-font-medium tw-text-md'>Short URL</span>
                <Alert variant='info'>
                  {preview}
                </Alert>
                <div className='tw-flex tw-gap-2 tw-justify-end tw-mt-4'>
                  <Button onClick={onCopyUrlClick}>
                    Copy
                  </Button>
                  <Button
                    variant='secondary'
                    onClick={handleClear}
                  >
                    Clear
                  </Button>
                </div>
              </Col>
            </Row>
          )}
        </Container>
      </Body>
    </>
  );
};

export default Home;
